// ============================================================================
// Shared Types for Marketplace API
// ============================================================================

import {
  PostItemCondition,
  PostItemPickupOption,
} from '@/types/residents/marketplaceListing';

// ============================================================================
// API Response Types
// ============================================================================

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  warning?: string;
  message?: string;
}

// ============================================================================
// Listings Types
// ============================================================================

// Listings query parameters
export interface ListingsQueryParams {
  propertyId?: number;
  pageNum?: number;
  searchQuery?: string;
  condition?: string;
  minPrice?: number;
  maxPrice?: number;
  sellerId?: number;
}

// Create listing request
export interface CreateListingRequest {
  title: string;
  description?: string;
  price?: number;
  condition?: string;
  pickupOption?: string;
  propertyId: number;
  images?: Array<{
    uri: string;
    type: string;
    name: string;
    file?: File; // For web uploads
  }>;
}

// Update listing request
export interface UpdateListingRequest {
  id: string;
  title?: string;
  description?: string;
  price?: number;
  condition?: string;
  pickupOption?: string;
  existingImageIds?: number[];
  images?: Array<{
    uri: string;
    type: string;
    name: string;
    file?: File; // For web uploads
  }>;
}

// ============================================================================
// Messages Types
// ============================================================================

// File attachment for messages
export interface MessageFileAttachment {
  uri: string;
  type: string;
  name: string;
  file?: File; // For web uploads
}

// Start conversation request
export interface StartConversationRequest {
  listingId: string;
  message?: string;
  files?: MessageFileAttachment[];
}

// Send message request
export interface SendMessageRequest {
  conversationId: string;
  text?: string;
  files?: MessageFileAttachment[];
  replyToId?: string;
  /** Optional listingId for cache invalidation when sending from inline conversation */
  listingId?: string;
}

// Get conversation request (with pagination)
export interface GetConversationRequest {
  conversationId: string;
  pageNum?: number;
}

// Conversation pagination info
export interface ConversationPagination {
  pageNum: number;
  pageSize: number;
  totalMessages: number;
  totalPages: number;
  hasMore: boolean;
}

// ============================================================================
// Social Types
// ============================================================================

// Create review request
export interface CreateReviewRequest {
  revieweeId: number;
  listingId: string;
  rating: number;
  text?: string;
}

// ============================================================================
// Reports Types
// ============================================================================

import { ReportReason } from '@/types/residents/marketplaceListing';

// Create report request (supports both listing and conversation reports)
export interface CreateReportRequest {
  listingId?: string;
  conversationId?: string;
  reportedUserId?: string;
  reason: ReportReason;
  description?: string;
}

// ============================================================================
// Feedback Types
// ============================================================================

// Create feedback request
export interface CreateFeedbackRequestRequest {
  listingId: string;
  buyerId: string;
}

// Complete feedback request
export interface CompleteFeedbackRequestRequest {
  requestId: string;
  rating: number;
  text?: string;
}

// Marketplace notification response
export interface MarketplaceNotification {
  id: string;
  type: 'marketplace_leave_feedback' | 'marketplace_received_feedback';
  title: string;
  message: string;
  metadata?: {
    feedbackRequestId?: number;
    listingId?: number;
    listingTitle?: string;
    listingImageUrl?: string | null;
    sellerId?: number;
    reviewId?: number;
    buyerId?: number;
    buyerName?: string;
    buyerAvatarUrl?: string | null;
    rating?: number;
    reviewText?: string | null;
  };
  isRead: boolean;
  readAt?: string;
  createdAt: string;
}

// Feedback request response
export interface FeedbackRequest {
  id: string;
  listingId: string;
  sellerId: string;
  buyerId: string;
  status: 'pending' | 'completed' | 'expired';
  createdAt: string;
  listing: {
    id: string;
    title: string;
    image: string;
    condition: string;
  };
  seller: {
    id: string;
    name: string;
    initials: string;
    avatarUrl?: string;
  };
}

// ============================================================================
// Backend Response Types
// ============================================================================

// Backend profile response
export interface BackendProfileResponse {
  accountId: number;
  rating: number;
  reviewCount: number;
  soldCount: number;
  boughtCount: number;
  followersCount: number;
  followingCount: number;
}

// Backend conversation response
export interface BackendConversationResponse {
  id: number;
  listingId: number;
  buyerId: number;
  sellerId: number;
  lastMessageAt: string;
  listing: {
    id: number;
    title: string;
    images?: Array<{ imageUrl: string }>;
  };
  lastMessage?: {
    id: number;
    text: string;
    senderId: number;
    createdAt: string;
  };
  unreadCount: number;
  otherUserId: number;
  otherUserName: string;
  otherUserInitials: string;
  otherUserAvatarUrl?: string;
  isUnread: boolean;
  currentUserId?: number;
  messages?: BackendMessageResponse[];
}

// Backend response with pagination
export interface BackendConversationWithPaginationResponse {
  success: boolean;
  data: BackendConversationResponse;
  pagination: {
    pageNum: number;
    pageSize: number;
    totalMessages: number;
    totalPages: number;
    hasMore: boolean;
  };
}

// Backend message file response
export interface BackendMessageFileResponse {
  id: number;
  messageId: number;
  fileUrl: string;
  fileName: string;
}

// Backend reply-to message response (nested in message)
export interface BackendReplyToResponse {
  id: number;
  senderId: number;
  text: string;
}

// Backend message response
export interface BackendMessageResponse {
  id: number;
  conversationId: number;
  senderId: number;
  text: string;
  readAt: string | null;
  createdAt: string;
  files?: BackendMessageFileResponse[];
  replyTo?: BackendReplyToResponse;
}

// Backend following response
export interface BackendFollowingResponse {
  userId: number;
  name: string;
  initials: string;
  avatarUrl?: string;
  profile?: {
    accountId: number;
    rating: number;
    reviewCount: number;
  };
  hasNewListing?: boolean;
  followedAt: string;
}

// Backend review response
export interface BackendReviewResponse {
  id: number;
  reviewerId: number;
  revieweeId: number;
  listingId: number;
  rating: number;
  text?: string;
  createdAt: string;
  listing?: {
    title: string;
    image?: string;
  };
  reviewerName: string;
  reviewerInitials: string;
  reviewerAvatarUrl?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Build FormData for listing creation/update with images
 */
export const buildListingFormData = (
  data: CreateListingRequest | Omit<UpdateListingRequest, 'id'>
): FormData => {
  const formData = new FormData();
  
  if ('title' in data && data.title) {
    formData.append('title', data.title);
  }
  if ('description' in data && data.description !== undefined) {
    formData.append('description', data.description);
  }
  if ('price' in data && data.price !== undefined) {
    formData.append('price', String(data.price));
  }
  if ('condition' in data && data.condition) {
    formData.append('condition', data.condition);
  }
  if ('pickupOption' in data && data.pickupOption) {
    formData.append('pickupOption', data.pickupOption);
  }
  if ('propertyId' in data && data.propertyId) {
    formData.append('propertyId', String(data.propertyId));
  }
  if ('existingImageIds' in data && data.existingImageIds) {
    formData.append('existingImageIds', JSON.stringify(data.existingImageIds));
  }
  
  // Append images - handle both web (File object) and native (uri/type/name) formats
  if (data.images && data.images.length > 0) {
    data.images.forEach((image: any) => {
      if (image.file) {
        // Web: use the actual File object directly
        formData.append('images', image.file);
      } else if (image.uri) {
        // React Native: use the { uri, type, name } format
        formData.append('images', {
          uri: image.uri,
          type: image.type || 'image/jpeg',
          name: image.name || 'image.jpg',
        } as any);
      }
    });
  }
  
  return formData;
};

/**
 * Map PostItemCondition to backend condition format
 */
export const mapConditionToBackend = (condition: PostItemCondition): string => {
  switch (condition) {
    case 'brand_new':
      return 'new';
    case 'new_without_tags':
      return 'new_without_tags';
    case 'used':
      return 'used';
    default:
      return 'used';
  }
};

/**
 * Map pickup options to backend format
 */
export const mapPickupOptionsToBackend = (options: PostItemPickupOption[]): string => {
  if (options.length === 0) return 'room_pickup';
  if (options.length >= 3 || options.includes('meet_in_person')) {
    return 'any_pickup';
  }
  if (options.includes('lobby_pickup') && !options.includes('room_pickup')) {
    return 'lobby_pickup';
  }
  return 'room_pickup';
};

/**
 * Build FormData for sending messages with file attachments
 */
export const buildMessageFormData = (
  text?: string,
  files?: MessageFileAttachment[],
  replyToId?: string,
): FormData => {
  const formData = new FormData();
  
  if (text) {
    formData.append('text', text);
  }

  if (replyToId) {
    formData.append('replyToId', replyToId);
  }
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      if (file.file) {
        // Web: use the actual File object directly
        formData.append('files', file.file);
      } else if (file.uri) {
        // React Native: use the { uri, type, name } format
        formData.append('files', {
          uri: file.uri,
          type: file.type || 'image/jpeg',
          name: file.name || 'file.jpg',
        } as any);
      }
    });
  }
  
  return formData;
};

